import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePrevious } from "react-use";
import {
  HMSRoomState,
  selectAppData,
  selectIsConnectedToRoom,
  selectRoomState,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { FullPageSpinner } from "@/components/common/FullPageSpinner";
import {
  InterviewInfoDisplay,
  isPanelAvailable,
} from "@/components/InterviewInfoDisplay";
import { ChatPanel, PINNED_MESSAGE_KEY } from "@/components/meeting/ChatPanel";
import { ControlBar } from "@/components/meeting/ControlBar";
import { MeetingHeader } from "@/components/meeting/MeetingHeader";
import { ParticipantsPanel } from "@/components/meeting/ParticipantsPanel";
import { VideoStage } from "@/components/meeting/VideoStage";
import { useAutoRecording } from "@/hooks/useAutoRecording";
import { useInterviewInfo, useInterviewRecording } from "@/hooks/useInterview";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useMyMetadata } from "@/hooks/useMetadata";
import { SIDE_PANES, useSidePane } from "@/hooks/useSidePane";

const SidePanel = () => {
  const { sidePane, setSidePane } = useSidePane();
  if (!sidePane) {
    return null;
  }
  const close = () => setSidePane(null);
  return (
    <aside
      className="absolute inset-x-3 bottom-3 z-20 h-[70%] overflow-hidden rounded-lg border bg-card shadow-xl lg:static lg:inset-auto lg:z-auto lg:h-auto lg:w-[340px] lg:shrink-0 lg:shadow-none"
      data-testid="side_pane"
    >
      {sidePane === SIDE_PANES.chat ? <ChatPanel onClose={close} /> : null}
      {sidePane === SIDE_PANES.participants ? (
        <ParticipantsPanel onClose={close} />
      ) : null}
    </aside>
  );
};

/** The call: who you are talking to, the video, and the controls. */
const Conference = () => {
  const navigate = useNavigate();
  const { roomId, role } = useParams();
  const hmsActions = useHMSActions();
  const roomState = useHMSStore(selectRoomState);
  const previousState = usePrevious(roomState);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const isHeadless = useHMSStore(selectAppData("isHeadless"));
  const info = useInterviewInfo(roomId);
  const recording = useInterviewRecording(roomId);
  const { isInterviewOn } = useMyMetadata();
  useKeyboardShortcuts();
  useAutoRecording(
    recording.status === "ready" && Boolean(recording.data?.recording_enabled)
  );

  // Opened without joining from the preview: go there.
  useEffect(() => {
    if (
      !previousState &&
      !(
        roomState === HMSRoomState.Connecting ||
        roomState === HMSRoomState.Reconnecting ||
        isConnected
      )
    ) {
      navigate(`/preview/${roomId}/${role}`, { replace: true });
    }
  }, [isConnected, previousState, roomState, navigate, role, roomId]);

  useEffect(() => {
    if (isHeadless) {
      // The recording bot doesn't need chat in its store.
      hmsActions.ignoreMessageTypes(["chat"]);
    }
  }, [isHeadless, hmsActions]);

  useEffect(() => {
    if (isConnected) {
      hmsActions.sessionStore.observe([PINNED_MESSAGE_KEY]).catch(() => {});
    }
  }, [isConnected, hmsActions]);

  // Leaving the page (back button, closing the tab) leaves the room.
  const connected = useRef(isConnected);
  connected.current = isConnected;
  useEffect(
    () => () => {
      if (connected.current) {
        hmsActions.leave();
      }
    },
    [hmsActions]
  );

  if (!isConnected) {
    return <FullPageSpinner label="Joining the interview" />;
  }

  const panelAvailable = !isHeadless && isPanelAvailable(info, role);
  const showPanel = panelAvailable && isInterviewOn;

  return (
    <div className="flex h-full flex-col bg-background">
      {!isHeadless ? <MeetingHeader /> : null}
      <main
        className="relative flex min-h-0 flex-1 flex-col gap-3 p-3 lg:flex-row"
        data-testid="conferencing"
      >
        {showPanel ? (
          <aside className="max-h-[40%] shrink-0 overflow-hidden rounded-lg border bg-card lg:max-h-none lg:w-[360px]">
            <InterviewInfoDisplay info={info} role={role} />
          </aside>
        ) : null}
        <section className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg bg-stage">
          <VideoStage />
        </section>
        {!isHeadless ? <SidePanel /> : null}
      </main>
      {!isHeadless ? <ControlBar panelAvailable={panelAvailable} /> : null}
    </div>
  );
};

export default Conference;
